<script setup lang="ts">
import { computed, ref, inject, type ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import JobsFilters from './jobs/JobsFilters.vue';
import JobsGrid from './jobs/JobsGrid.vue';
import JobsLoading from './jobs/JobsLoading.vue';
import CitySelector from '@/core/components/form-elements/CitySelector.vue';
import { NxtIconButton, NxtInput, NxtSpinner, NxtPaging } from '@future-harry/nxt-styles';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import { useJobs } from '@/modules/content-blocks/composables/useJobs';
import { useJobFilters } from '@/modules/content-blocks/composables/useJobFilters';

const viewport = inject<ComputedRef<'desktop' | 'tablet' | 'mobile'>>('viewport');

const route = useRoute();
const router = useRouter();

const props = defineProps({
   data: {
      type: Object,
      default: () => ({}),
   },
   preview: {
      type: Boolean,
      default: false,
   },
});

// "filters" state management (handles search queries and filter selections)
//    "filters" is the (parent)group of jobTypes, workSchedules, searchQuery, and searchCity
const { filters, jobTypes, cities, workSchedules, searchQuery, searchCity } = useJobFilters();

const currentPage = computed(() => +(route.query.page || 1));

function handleSetPage(page: number) {
   router.push({ ...route, query: { ...route.query, page } });
}

// data fetching: refetches jobs on "filters" changes
const { aggregations, jobs, isLoading, availableFilters, meta } = useJobs(filters, currentPage);

const jobBoardStore = useJobBoardStore();
const categories = computed(() => jobBoardStore.categories);

const toggleViewMode = () => {
   viewMode.value = viewMode.value === 'grid' ? 'list' : 'grid';
};

const viewMode = ref<'grid' | 'list'>('grid');

// if no filters (editable) are shown, take full width `jobs-content--full
const showAnyFilter = computed(() => {
   return props.data.job_types || props.data.schedules || props.data.cities;
});

const spontaneousApplicationUrl = computed(() => {
   return props.data.spontaneous_application_url || '#';
});
</script>

<template>
   <main :class="['jobs-content', { 'jobs-content--full': !showAnyFilter, 'jobs-content--mobile': viewport === 'mobile' }]">
      <NxtSpinner v-if="false" />
      <template v-else>
         <JobsFilters
            v-if="showAnyFilter"
            v-model:job-types="jobTypes"
            v-model:work-schedules="workSchedules"
            v-model:cities="cities"
            :available-filters="availableFilters"
            :show-job-type-filter="data.job_types"
            :show-schedule-filter="data.schedules"
            :show-city-filter="data.cities"
            :aggregations="aggregations"
         />
         <section>
            <div class="jobs-list-header">
               <h2>{{ jobs.length }} {{ $tb('live-editor.content-blocks.job_board.jobs', jobs.length) }}</h2>
               <div class="jobs-list-header__actions">
                  <CitySelector class="mr-gutter" v-if="data.search_cities" v-model="searchCity" />
                  <NxtInput
                     v-if="data.search"
                     v-model="searchQuery"
                     name="job_search_input"
                     :placeholder="$tb('live-editor.content-blocks.job_board.search-job-placeholder')"
                  />
                  <NxtIconButton class="toggle-gridlist" :name="viewMode === 'grid' ? 'list' : 'grid'" @click="toggleViewMode" />
               </div>
            </div>
            <JobsLoading v-if="isLoading" />
            <JobsGrid
               v-else
               :categories="categories"
               :jobs="jobs"
               :show-spontaneous="data.show_spontaneous_application"
               :spontaneous-url="spontaneousApplicationUrl"
               :view-mode="viewMode"
            />
            <NxtPaging
               v-if="meta.total >= meta.per_page"
               :current_page="currentPage"
               :total="meta.total"
               :per_page="meta.per_page"
               @on-set-page="handleSetPage"
            />
         </section>
      </template>
   </main>
</template>

<style scoped lang="scss">
.jobs-content {
   max-width: 1000px;
   margin: 0 auto;
   display: grid;
   grid-template-columns: 200px 1fr;
   gap: var(--nxt-gutter-xlarge);

   :deep(input) {
      background-color: transparent;
   }

   &--full {
      grid-template-columns: 1fr;
   }

   .mr-gutter {
      margin-right: var(--nxt-gutter);
   }

   .jobs-list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--nxt-gutter-large);

      &__actions {
         display: flex;
         justify-content: end;
      }
   }

   @mixin mobile-styles {
      grid-template-columns: 1fr;
      gap: var(--nxt-gutter-small);

      .jobs-list-header {
         flex-direction: column;
         align-items: flex-start;
      }

      &:deep(.jobs-filters) {
         padding: 0 var(--nxt-gutter-small);
      }
   }

   &--mobile {
      @include mobile-styles;
   }

   @media (max-width: 400px) {
      @include mobile-styles;
   }

   :deep(.nxt-spinner) {
      margin: 0 auto;
      color: var(--jobsite-primary, var(--nxt-main));
   }

   :deep(.nxt-paging) {
      margin-top: var(--nxt-gutter);

      .nxt-paging__page {
         background-color: var(--nxt-light-grey);
      }

      .nxt-paging__page.-selected {
         background-color: var(--nxt-dark);
      }

      .nxt-paging__navigation {
         background-color: var(--nxt-light-grey);
      }
   }
}
</style>
